import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Cpu, HeartPulse, Clapperboard, Rocket, Sparkles, Shield, Radar, BadgeCheck } from "lucide-react";
import axios from "axios";
import Ticker from "../components/Ticker";
import HealthDashboardMock from "../components/HealthDashboardMock";
import ScoutingPortalMock from "../components/ScoutingPortalMock";
import Seo from "../components/Seo";
import { Section, Eyebrow, H1, H2, Lead, ButtonPrimary, ButtonGhost, GlassCard } from "../components/ui-kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1755406544715-e1f963e32118?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBvcmFuZ2UlMjBuZW9uJTIwZGF0YSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc3NzIzMTUyfDA&ixlib=rb-4.1.0&q=85";
const ACADEMY_IMG = "https://images.pexels.com/photos/9072293/pexels-photo-9072293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LAB_IMG = "https://images.unsplash.com/photo-1534423861386-85a16f5d13fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxlc3BvcnRzJTIwYXJlbmF8ZW58MHx8fHwxNzc3NzIzMTUyfDA&ixlib=rb-4.1.0&q=85";
const STUDENT_IMG = "https://images.unsplash.com/photo-1729855011439-0cdc2a1d2d68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjB0ZWVuYWdlciUyMGVzcG9ydHMlMjBnYW1lcnxlbnwwfHx8fDE3Nzc3MjMxMzl8MA&ixlib=rb-4.1.0&q=85";
// Content creation / podcast-studio setup for Content & Agency tile
const STUDIO_IMG = "https://images.pexels.com/photos/3945313/pexels-photo-3945313.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const [ticker, setTicker] = useState([
    "12-Month Pro-Path Program",
    "Performance · Wellness · Pathways",
    "Curriculum Compliance Mapping",
    "Wellness modeled on ISO 45001",
    "Pilot Cohort · 15 Seats",
    "Partnership response within 48 hours",
  ]);

  useEffect(() => {
    axios
      .get(`${API}/stats`)
      .then((r) => r.data?.tagline_items && setTicker(r.data.tagline_items))
      .catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title={null}
        description="India has the world's largest gaming generation — and no structured path to turn that talent into careers."
        url="https://perforange.com/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute inset-0 bg-radial-orange" />
        <img
          src={HERO_IMG}
          alt="Abstract dark orange neon data network representing the software backbone for school esports programs"
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen pointer-events-none"
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#0A0A0C]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="max-w-4xl fade-up">
            <Eyebrow>For Forward-Thinking Schools</Eyebrow>
            <H1>
              Built for schools that take <span className="text-gradient-orange">student development</span> seriously.
            </H1>
            <p className="mt-6 text-lg md:text-xl text-[#D4D4D8] max-w-3xl leading-relaxed">
              The infrastructure your esports program has been missing — a structured pathway parents can trust, students can grow inside, and a principal can defend on day one.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/principals-corner" data-testid="hero-cta-primary">
                <ButtonPrimary>
                  Get Empaneled <ArrowUpRight className="w-4 h-4" />
                </ButtonPrimary>
              </Link>
              <Link to="/content-agency" data-testid="hero-cta-secondary">
                <ButtonGhost>
                  Partner with Us <ArrowRight className="w-4 h-4" />
                </ButtonGhost>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { k: "12", v: "Month Pro-Path" },
                { k: "K–12", v: "Curriculum Spectrum" },
                { k: "3", v: "Pillars · Performance · Wellness · Pathways" },
                { k: "48 hrs", v: "Partnership Response" },
              ].map((s, i) => (
                <div key={i} className="border-l border-white/10 pl-4">
                  <p className="text-3xl md:text-4xl font-black font-display text-white">{s.k}</p>
                  <p className="text-xs uppercase tracking-widest text-[#A1A1AA] mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Ticker items={ticker} />
      </section>

      {/* VERTICALS BENTO */}
      <Section>
        <div className="max-w-3xl mb-12">
          <Eyebrow>Three Verticals · One Operating System</Eyebrow>
          <H2>We don&apos;t sell hardware. We sell excellence.</H2>
          <Lead className="mt-5">
            An asset-light operating system that layers over the labs schools already have — the syllabus, the wellness framework, and the pathway from classroom to career.
          </Lead>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6" data-testid="verticals-bento">
          {/* Academy - big */}
          <Link
            to="/academy"
            data-testid="vertical-academy"
            className="md:col-span-8 relative overflow-hidden rounded-2xl glass group hover:border-[#FF8C00]/40 transition-all min-h-[360px]"
          >
            <img src={ACADEMY_IMG} alt="Pro-Path syllabus and trainer certification program for school esports academies" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-transparent" />
            <div className="relative h-full p-8 md:p-10 flex flex-col justify-end">
              <Cpu className="w-8 h-8 text-[#FF8C00] mb-3" />
              <p className="text-xs uppercase tracking-widest text-[#FF8C00] font-bold">01 · The Academy</p>
              <h3 className="mt-2 text-3xl md:text-4xl font-display font-black text-white">Train the Trainer. Certify the Program.</h3>
              <p className="mt-3 text-[#A1A1AA] max-w-xl">
                A 12-month Pro-Path syllabus in Performance, Ethics, and Tactics — cascaded through a Train-the-Trainer model that turns any school into a credible feeder pipeline.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#FF8C00]">
                Explore the Academy <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* Performance Lab */}
          <Link
            to="/academy#performance-lab"
            data-testid="vertical-performance-lab"
            className="md:col-span-4 relative overflow-hidden rounded-2xl glass group hover:border-[#00FFD0]/40 transition-all min-h-[360px]"
          >
            <img src={LAB_IMG} alt="State-of-the-art eSports performance arena with wellness tracking technology" className="absolute inset-0 w-full h-full object-cover opacity-35 group-hover:opacity-50 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/75 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#00FFD0]/10 via-transparent to-transparent" />
            <div className="relative h-full p-8 md:p-10 flex flex-col justify-end">
              <HeartPulse className="w-8 h-8 text-[#00FFD0] mb-3" />
              <p className="text-xs uppercase tracking-widest text-[#00FFD0] font-bold">02 · Performance Lab</p>
              <h3 className="mt-2 text-2xl md:text-3xl font-display font-black text-white">Wellness-Tech for the Digital Athlete.</h3>
              <p className="mt-3 text-sm text-[#A1A1AA] max-w-xl">
                Posture, fatigue, eye-strain — measured, coached, designed to align with ISO 45001 ergonomic principles.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#00FFD0]">
                Enter the Lab <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* Content & Agency */}
          <Link
            to="/content-agency"
            data-testid="vertical-content-agency"
            className="md:col-span-5 relative overflow-hidden rounded-2xl glass group hover:border-[#FF8C00]/40 transition-all min-h-[300px]"
          >
            <img src={STUDIO_IMG} alt="Digital production studio and scouting portal for Indian content creators" className="absolute inset-0 w-full h-full object-cover opacity-35 group-hover:opacity-50 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/75 to-transparent" />
            <div className="relative h-full p-8 md:p-10 flex flex-col justify-end">
              <Clapperboard className="w-8 h-8 text-[#FF8C00] mb-3" />
              <p className="text-xs uppercase tracking-widest text-[#FF8C00] font-bold">03 · Career Ecosystem</p>
              <h3 className="mt-2 text-2xl md:text-3xl font-display font-black text-white">From Gameplay to Digital IP.</h3>
              <p className="mt-3 text-sm text-[#A1A1AA] max-w-xl">
                A student-side Production House and an org-side Scouting Portal — powered by certified Perforange metrics.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#FF8C00]">
                Open the Studio <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* Student journey tile */}
          <div className="md:col-span-7 relative overflow-hidden rounded-2xl glass min-h-[300px]">
            <img src={STUDENT_IMG} alt="Student esports athlete representing the structured path from school program to verified career pathway" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/80 to-transparent" />
            <div className="relative h-full p-8 flex flex-col justify-center max-w-lg">
              <Rocket className="w-7 h-7 text-[#FF8C00] mb-3" />
              <h3 className="text-2xl md:text-3xl font-display font-black text-white">Classroom → Career Pathway.</h3>
              <p className="mt-3 text-[#A1A1AA]">
                A verifiable pipeline: Screening → 12-Month Pro-Path → Certification → Scouting Portal → Signing.
              </p>
              <Link
                to="/academy"
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#FF8C00] hover:text-[#FFA333]"
                data-testid="view-journey-link"
              >
                See the Infographic <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* HEALTH DASHBOARD PREVIEW */}
      <Section>
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <Eyebrow color="#00FFD0">Performance Lab · Overview</Eyebrow>
            <H2>Measured wellness. Not folklore.</H2>
            <Lead className="mt-5">
              Perforange turns every session into data a principal can defend at a parent-teacher meeting — and a student can act on the next day.
            </Lead>
            <Link to="/academy#performance-lab" className="inline-flex mt-6" data-testid="home-lab-cta">
              <ButtonGhost>
                See How It Works <ArrowUpRight className="w-4 h-4" />
              </ButtonGhost>
            </Link>
          </div>
          <div className="md:col-span-7">
            <HealthDashboardMock />
          </div>
        </div>
      </Section>

      {/* SCOUTING PORTAL PREVIEW */}
      <Section>
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <ScoutingPortalMock />
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <Eyebrow>For eSports Orgs</Eyebrow>
            <H2>Scout certified talent, not Twitch clips.</H2>
            <Lead className="mt-5">
              Filter by state, game, Perforange Score, and wellness clearance. Every profile is backed by classroom-verified metrics.
            </Lead>
            <Link to="/content-agency" className="inline-flex mt-6" data-testid="home-scout-cta">
              <ButtonPrimary>
                Request Partner Access <ArrowUpRight className="w-4 h-4" />
              </ButtonPrimary>
            </Link>
          </div>
        </div>
      </Section>

      {/* PERFORANGE OS PREVIEW */}
      <Section>
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <Eyebrow><Sparkles className="w-3 h-3" /> Segment 05 · Proprietary LMS</Eyebrow>
            <H2>Perforange OS — the central nervous system of every CCL.</H2>
            <Lead className="mt-5">
              Active firewall, AI-vision wellness telemetry, radar analytics, and a Command Dashboard that lets one faculty member orchestrate 40+ workstations. Complete control for administrators, complete immersion for students.
            </Lead>
            <ul className="mt-6 space-y-3">
              {[
                { icon: Shield, t: "Active Firewall & Lockout — hard-locked to curriculum." },
                { icon: HeartPulse, t: "AI-Vision Wellness — posture, eye-strain, fatigue." },
                { icon: Radar, t: "Radar Analytics — every student gets a Scouting ID." },
                { icon: BadgeCheck, t: "Perforange Completion Badge — issued with curriculum compliance mapping." },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#FF8C00]" />
                    </div>
                    <p className="text-sm text-[#D4D4D8] mt-1">{b.t}</p>
                  </li>
                );
              })}
            </ul>
            <Link to="/perforange-os" className="inline-flex mt-7" data-testid="home-os-cta">
              <ButtonPrimary>
                Explore Perforange OS <ArrowUpRight className="w-4 h-4" />
              </ButtonPrimary>
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="relative grid grid-cols-2 gap-4">
              {/* Mini feature tiles preview */}
              {[
                { icon: Shield, t: "Firewall", v: "ON", c: "#FF8C00" },
                { icon: HeartPulse, t: "Wellness", v: "92%", c: "#00FFD0" },
                { icon: Radar, t: "Scouting ID", v: "AA-24-0418", c: "#FFB05E" },
                { icon: Cpu, t: "Stations", v: "40 / 40", c: "#00FFD0" },
              ].map((x, i) => {
                const Icon = x.icon;
                return (
                  <div key={i} className="glass rounded-2xl p-5 hover:border-[#FF8C00]/40 transition-all" data-testid={`os-preview-tile-${i}`}>
                    <Icon className="w-5 h-5" style={{ color: x.c }} />
                    <p className="mt-4 font-display font-black text-3xl text-white">{x.v}</p>
                    <p className="text-[11px] uppercase tracking-widest text-[#A1A1AA] mt-1">{x.t}</p>
                    <div className="mt-3 h-1 rounded-full bg-white/5">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${60 + (i * 11) % 35}%`, background: x.c, boxShadow: `0 0 8px ${x.c}` }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="absolute inset-0 -z-10 bg-radial-orange opacity-60" />
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="relative overflow-hidden rounded-3xl glass-strong p-10 md:p-16">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#FF8C00]/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#00FFD0]/10 blur-3xl" />
          <div className="relative max-w-3xl">
            <Eyebrow>Build the Esports Program Your School Needs</Eyebrow>
            <H2>Your lab is ready. The program around it isn&apos;t.</H2>
            <p className="mt-5 text-lg text-[#A1A1AA]">
              Empanel your school, onboard a trainer cohort, or partner with us.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/principals-corner" data-testid="bottom-cta-schools">
                <ButtonPrimary>Get Empaneled <ArrowUpRight className="w-4 h-4" /></ButtonPrimary>
              </Link>
              <Link to="/contact" data-testid="bottom-cta-contact">
                <ButtonGhost>Talk to Mission Control <ArrowRight className="w-4 h-4" /></ButtonGhost>
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
